/**
 * Chat history status + custom filters — parity with **chat-sdk-ios**
 * `ChatHistoryStatusFilterHelper` + `KustomerOptions` filter semantics.
 */

export type HistoryStatusFilterValue =
  | 'all'
  | 'open'
  | 'closed'
  | 'requiresAttention';

export interface CustomHistoryFilterClause {
  readonly key: string;
  readonly value: string;
}

export type ConversationCustomAttributeValue =
  | { readonly kind: 'string'; readonly value: string }
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'boolean'; readonly value: boolean }
  | { readonly kind: 'date'; readonly value: string };

export type ConversationCustomAttributes = Readonly<
  Record<string, ConversationCustomAttributeValue>
>;

export interface ChatHistoryFilterConversation {
  readonly conversationId: string;
  readonly ended?: boolean;
  readonly custom?: ConversationCustomAttributes;
}

export interface ChatHistoryFilterOptions {
  readonly historyStatusFilter: HistoryStatusFilterValue;
  readonly customHistoryFilter?: readonly CustomHistoryFilterClause[];
}

const ISO8601_FULL =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const normalizeKey = (key: string) => key.trim();

const isConversationCustomAttributeValue = (
  v: unknown,
): v is ConversationCustomAttributeValue => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) {
    return false;
  }
  const o = v as Record<string, unknown>;
  const { kind } = o;
  if (kind === 'string' && typeof o.value === 'string') {
    return true;
  }
  if (
    kind === 'number' &&
    typeof o.value === 'number' &&
    !Number.isNaN(o.value)
  ) {
    return true;
  }
  if (kind === 'boolean' && typeof o.value === 'boolean') {
    return true;
  }
  if (kind === 'date' && typeof o.value === 'string') {
    return true;
  }
  return false;
};

export const normalizeSessionCustomAttributes = (
  raw: unknown,
): ConversationCustomAttributes | undefined => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const entries = Object.entries(raw as Record<string, unknown>);
  if (!entries.length) {
    return undefined;
  }
  const out: Record<string, ConversationCustomAttributeValue> = {};
  entries.forEach(([key, v]) => {
    const k = normalizeKey(key);
    if (!k) {
      return;
    }
    if (isConversationCustomAttributeValue(v)) {
      out[k] = v;
      return;
    }
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (ISO8601_FULL.test(trimmed)) {
        out[k] = { kind: 'date', value: trimmed };
      } else {
        out[k] = { kind: 'string', value: v };
      }
      return;
    }
    if (typeof v === 'number' && !Number.isNaN(v)) {
      out[k] = { kind: 'number', value: v };
      return;
    }
    if (typeof v === 'boolean') {
      out[k] = { kind: 'boolean', value: v };
    }
  });
  return Object.keys(out).length ? out : undefined;
};

export const areConversationCustomAttributesEqual = (
  first: ConversationCustomAttributes | undefined,
  second: ConversationCustomAttributes | undefined,
): boolean => {
  const firstKeys = Object.keys(first ?? {}).sort();
  const secondKeys = Object.keys(second ?? {}).sort();

  if (firstKeys.length !== secondKeys.length) {
    return false;
  }

  return firstKeys.every((key, index) => {
    const secondKey = secondKeys[index];
    if (key !== secondKey) {
      return false;
    }
    return JSON.stringify(first?.[key]) === JSON.stringify(second?.[key]);
  });
};

const parseBoolFilter = (raw: string): boolean | undefined => {
  switch (raw.trim().toLowerCase()) {
    case 'true':
    case '1':
    case 'yes':
    case 'y':
      return true;
    case 'false':
    case '0':
    case 'no':
    case 'n':
      return false;
    default:
      return undefined;
  }
};

const customAttributeMatchesFilter = (
  rowValue: ConversationCustomAttributeValue,
  filterValue: string,
): boolean => {
  const chosen = filterValue.trim();
  switch (rowValue.kind) {
    case 'string':
      return rowValue.value.toLowerCase().startsWith(chosen.toLowerCase());
    case 'number': {
      if (!chosen) {
        return false;
      }
      const parsed = Number(chosen);
      return !Number.isNaN(parsed) && rowValue.value === parsed;
    }
    case 'boolean': {
      const parsed = parseBoolFilter(chosen);
      return parsed !== undefined && rowValue.value === parsed;
    }
    case 'date':
      return rowValue.value === chosen;
    default:
      return false;
  }
};

const rowMatchesCustomHistoryFilter = (
  conversation: ChatHistoryFilterConversation,
  clauses: readonly CustomHistoryFilterClause[] | undefined,
): boolean => {
  if (!clauses?.length) {
    return true;
  }
  const attrs = conversation.custom;
  if (!attrs) {
    return false;
  }
  for (let i = 0; i < clauses.length; i += 1) {
    const clause = clauses[i];
    const rowValue = attrs[normalizeKey(clause.key)];
    if (!rowValue) {
      return false;
    }
    if (!customAttributeMatchesFilter(rowValue, clause.value)) {
      return false;
    }
  }
  return true;
};

const isRowFilteredForStatusOnly = (
  conversation: ChatHistoryFilterConversation,
  filter: HistoryStatusFilterValue,
  unreadCount: number,
): boolean => {
  switch (filter) {
    case 'all':
      return false;
    case 'open':
      return !!conversation.ended;
    case 'closed':
      return !conversation.ended;
    case 'requiresAttention':
      return unreadCount <= 0;
    default:
      return false;
  }
};

/** When `true`, the row should be collapsed (zero height) — iOS `isRowFiltered`. */
export const isChatHistoryRowFiltered = (
  conversation: ChatHistoryFilterConversation,
  unreadCount: number,
  options: ChatHistoryFilterOptions,
): boolean => {
  if (
    !rowMatchesCustomHistoryFilter(conversation, options.customHistoryFilter)
  ) {
    return true;
  }
  return isRowFilteredForStatusOnly(
    conversation,
    options.historyStatusFilter,
    unreadCount,
  );
};

export const countHistoryRowsMatchingFilters = (
  conversations: readonly ChatHistoryFilterConversation[],
  unreadByConversationId: Readonly<Record<string, number>>,
  options: ChatHistoryFilterOptions,
): number =>
  conversations.reduce((acc, c) => {
    const unread = unreadByConversationId[c.conversationId] ?? 0;
    return acc + (isChatHistoryRowFiltered(c, unread, options) ? 0 : 1);
  }, 0);

export const customAttributesOutsideActiveFilter = (
  custom: ConversationCustomAttributes | undefined,
  clauses: readonly CustomHistoryFilterClause[] | undefined,
): ConversationCustomAttributes => {
  if (!custom) {
    return {};
  }
  if (!clauses?.length) {
    return custom;
  }

  const out: Record<string, ConversationCustomAttributeValue> = {};
  Object.entries(custom).forEach(([key, value]) => {
    const clausesForKey = clauses.filter(
      (clause) => normalizeKey(clause.key) === key,
    );
    if (!clausesForKey.length) {
      out[key] = value;
      return;
    }

    const allClausesMatch = clausesForKey.every((clause) =>
      customAttributeMatchesFilter(value, clause.value),
    );
    if (!allClausesMatch) {
      out[key] = value;
    }
  });

  return out;
};

export const parseHistoryStatusFilterValue = (
  raw: unknown,
): HistoryStatusFilterValue => {
  if (
    raw === 'all' ||
    raw === 'open' ||
    raw === 'closed' ||
    raw === 'requiresAttention'
  ) {
    return raw;
  }
  if (typeof raw === 'string') {
    const compact = raw.trim().toLowerCase().replace(/_/g, '');
    if (compact === 'none' || compact === 'all') {
      return 'all';
    }
    if (compact === 'open') {
      return 'open';
    }
    if (compact === 'closed') {
      return 'closed';
    }
    if (compact === 'requiresattention') {
      return 'requiresAttention';
    }
  }
  return 'all';
};

export interface GetHistoryFilterSummaryCountsInput {
  readonly conversations: readonly {
    readonly conversationId: string;
    readonly ended?: boolean;
    readonly movedToConversationId?: string;
    readonly custom?: unknown;
  }[];
  readonly noHistory: boolean;
  readonly unreadCounts: Readonly<Record<string, number>>;
  readonly statusFilter: HistoryStatusFilterValue;
  readonly customClauses: readonly CustomHistoryFilterClause[] | undefined;
}

export const getHistoryFilterSummaryCounts = (
  input: GetHistoryFilterSummaryCountsInput,
): { visible: number; total: number } => {
  const unreadIds = Object.keys(input.unreadCounts).filter(
    (id) => (input.unreadCounts[id] ?? 0) > 0,
  );
  const unreadSet = new Set(unreadIds);

  const base = input.conversations.filter((c) => !c.movedToConversationId);

  const passesNoHistory = (c: typeof base[number]) => {
    if (!input.noHistory) return true;
    const unread = unreadSet.has(c.conversationId);
    return !c.ended || unread;
  };

  const rows: ChatHistoryFilterConversation[] = base
    .filter(passesNoHistory)
    .map((c) => ({
      conversationId: c.conversationId,
      ended: c.ended,
      custom: normalizeSessionCustomAttributes(c.custom),
    }));

  const unreadMap = input.unreadCounts;

  const visible = countHistoryRowsMatchingFilters(rows, unreadMap, {
    historyStatusFilter: input.statusFilter,
    customHistoryFilter: input.customClauses,
  });

  const total = countHistoryRowsMatchingFilters(rows, unreadMap, {
    historyStatusFilter: 'all',
    customHistoryFilter: input.customClauses,
  });

  return { visible, total };
};
